import { useMemo } from "react";
import { getPlatform } from "@/lib/platform";
import { getWebApp } from "./webApp";

export type HapticImpactStyle = "light" | "medium" | "heavy" | "rigid" | "soft";
export type HapticNotificationType = "error" | "success" | "warning";

export interface HapticsApi {
  /** Tap feedback for primary CTAs. */
  impact: (style: HapticImpactStyle) => void;
  /** Result feedback for completed operations. */
  notification: (type: HapticNotificationType) => void;
  /** Subtle tick for tabs, segmented controls, list selection. */
  selection: () => void;
}

/**
 * Returns haptic helpers that are safe to call anywhere. In desktop preview
 * every call is a noop, so screens can sprinkle `haptics.impact("medium")`
 * inside CTA handlers without guarding for the SDK.
 *
 * Each call re-resolves the SDK so a hot-reload that toggles the Telegram
 * stub mid-session is reflected immediately.
 *
 * On iOS and on the web the returned methods route through
 * `getPlatform().haptics` instead of the Telegram SDK — the Telegram SDK
 * branch is unchanged (DUE-318).
 */
export function useHaptics(): HapticsApi {
  return useMemo(() => {
    if (getPlatform().name !== "telegram") {
      const haptics = getPlatform().haptics;
      return {
        impact: haptics.impact,
        notification: haptics.notification,
        selection: haptics.selectionChanged,
      };
    }
    return {
      impact: (style) => {
        const sdk = getWebApp();
        if (!sdk) return;
        try {
          sdk.HapticFeedback.impactOccurred(style);
        } catch {
          /* very old clients ship the symbol but throw on call */
        }
      },
      notification: (type) => {
        const sdk = getWebApp();
        if (!sdk) return;
        try {
          sdk.HapticFeedback.notificationOccurred(type);
        } catch {
          /* see above */
        }
      },
      selection: () => {
        const sdk = getWebApp();
        if (!sdk) return;
        try {
          sdk.HapticFeedback.selectionChanged();
        } catch {
          /* see above */
        }
      },
    };
  }, []);
}
